import { useEffect, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  creativeHashDataQueryOptions,
  type CreativeHashDataScope,
  type DrilldownQuerySection,
} from "@/lib/queries/drilldown.queries"
import type { CompetitorCreativeCardModel } from "../lib/drilldown-card.types"
import {
  creativeHashCacheKey,
  peekCachedCreativeHashData,
  setCachedCreativeHashData,
  touchCachedCreativeHashData,
} from "./useCreativeHashDataCache"

export function useCreativeHashDataSection(
  scope: CreativeHashDataScope,
  creative: CompetitorCreativeCardModel | null,
  open: boolean,
  startDate: string,
  endDate: string,
  dateRange: string,
): {
  section: DrilldownQuerySection | null
  isLoading: boolean
  isError: boolean
} {
  const hash = creative?.hash ?? ""
  const industry = creative?.industry ?? ""
  const libId = creative?.adId ?? null
  const cacheKey =
    hash && industry
      ? creativeHashCacheKey(hash, industry, startDate, endDate, dateRange, libId)
      : ""
  const cached = useMemo(
    () => (cacheKey ? peekCachedCreativeHashData(cacheKey) : undefined),
    [cacheKey],
  )

  useEffect(() => {
    if (cacheKey && cached) {
      touchCachedCreativeHashData(cacheKey)
    }
  }, [cacheKey, cached])

  const shouldFetch = open && !!cacheKey && !cached

  const { data, isLoading, isError } = useQuery(
    creativeHashDataQueryOptions(
      scope,
      hash,
      industry,
      startDate,
      endDate,
      dateRange,
      libId,
      shouldFetch,
    ),
  )

  useEffect(() => {
    if (data && cacheKey) {
      setCachedCreativeHashData(cacheKey, data)
    }
  }, [data, cacheKey])

  const section = cached ?? data ?? null

  return { section, isLoading: shouldFetch && isLoading, isError }
}
